import { ValidationError } from '../../api/errors/ApiError';
import { PAYMENT_TYPES } from '../../constants';

export const validateUserId = (userId: unknown): void => {
  if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
    throw new ValidationError('User ID is required and must be a non-empty string');
  }
};

export const validateCourseId = (courseId: unknown): void => {
  if (!courseId || typeof courseId !== 'string' || courseId.trim().length === 0) {
    throw new ValidationError('Course ID is required and must be a non-empty string');
  }
};

export const validateModuleId = (moduleId: unknown): void => {
  if (!moduleId || typeof moduleId !== 'string' || moduleId.trim().length === 0) {
    throw new ValidationError('Module ID is required and must be a non-empty string');
  }
};

export const validateLessonId = (lessonId: unknown): void => {
  if (!lessonId || typeof lessonId !== 'string' || lessonId.trim().length === 0) {
    throw new ValidationError('Lesson ID is required and must be a non-empty string');
  }
};

export const validateEmail = (email: unknown): void => {
  if (!email || typeof email !== 'string') {
    throw new ValidationError('Email is required and must be a string');
  }

  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  if (!emailRegex.test(email)) {
    throw new ValidationError('Email must be a valid email address');
  }
};

export const validatePassword = (password: unknown): void => {
  if (!password || typeof password !== 'string') {
    throw new ValidationError('Password is required and must be a string');
  }

  if (password.length < 8) {
    throw new ValidationError('Password must be at least 8 characters long');
  }

  if (!/[A-Z]/.test(password)) {
    throw new ValidationError('Password must contain at least one uppercase letter');
  }

  if (!/[a-z]/.test(password)) {
    throw new ValidationError('Password must contain at least one lowercase letter');
  }

  if (!/[0-9]/.test(password)) {
    throw new ValidationError('Password must contain at least one number');
  }
};

export const validateSessionId = (sessionId: unknown): void => {
  if (!sessionId || typeof sessionId !== 'string' || sessionId.trim().length === 0) {
    throw new ValidationError('Session ID is required and must be a non-empty string');
  }
};

interface QuizAttemptData {
  userId?: string;
  courseId?: string;
  quizId?: string;
  score?: number;
  [key: string]: unknown;
}

export const validateQuizAttemptData = (data: unknown): void => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new ValidationError('Quiz attempt data must be an object');
  }

  const attemptData = data as QuizAttemptData;
  const errors: string[] = [];

  if (!attemptData.userId) errors.push('userId is required');
  if (!attemptData.courseId) errors.push('courseId is required');
  if (!attemptData.quizId) errors.push('quizId is required');
  if (attemptData.score === undefined || attemptData.score === null) errors.push('score is required');
  if (attemptData.score !== undefined && (attemptData.score < 0 || attemptData.score > 100))
    errors.push('score must be between 0 and 100');

  if (errors.length > 0) {
    throw new ValidationError('Invalid quiz attempt data: ' + errors.join('; '), errors);
  }
};

export const validateEnrollmentData = (userId: unknown, courseId: unknown, userEmail: unknown): void => {
  const errors: string[] = [];

  if (!userId || typeof userId !== 'string') errors.push('userId is required');
  if (!courseId || typeof courseId !== 'string') errors.push('courseId is required');

  try {
    validateEmail(userEmail);
  } catch {
    errors.push('userEmail must be valid');
  }

  if (errors.length > 0) {
    throw new ValidationError('Invalid enrollment data: ' + errors.join('; '), errors);
  }
};

interface BreakData {
  userId?: string;
  sessionId?: string;
  breakType?: string;
  startTime?: string | number;
  duration?: number;
  [key: string]: unknown;
}

export const validateBreakData = (breakData: unknown): void => {
  if (!breakData || typeof breakData !== 'object' || Array.isArray(breakData)) {
    throw new ValidationError('Break data must be an object');
  }

  const data = breakData as BreakData;
  const errors: string[] = [];

  if (!data.userId) errors.push('userId is required');
  if (!data.sessionId) errors.push('sessionId is required');
  if (!data.breakType) errors.push('breakType is required');
  if (!data.startTime) errors.push('startTime is required');
  if (data.duration !== undefined && data.duration !== null && data.duration < 0) {
    errors.push('duration must be positive');
  }

  if (errors.length > 0) {
    throw new ValidationError('Invalid break data: ' + errors.join('; '), errors);
  }
};

interface LessonCompletionData {
  userId?: string;
  lessonId?: string;
  courseId?: string;
  [key: string]: unknown;
}

export const validateLessonCompletionData = (data: unknown): void => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new ValidationError('Lesson completion data must be an object');
  }

  const completionData = data as LessonCompletionData;
  const errors: string[] = [];

  if (!completionData.userId) errors.push('userId is required');
  if (!completionData.lessonId) errors.push('lessonId is required');
  if (!completionData.courseId) errors.push('courseId is required');

  if (errors.length > 0) {
    throw new ValidationError('Invalid lesson completion data: ' + errors.join('; '), errors);
  }
};

interface PVQData {
  userId?: string;
  questionId?: string;
  answer?: unknown;
  [key: string]: unknown;
}

export const validatePVQData = (data: unknown): void => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new ValidationError('PVQ data must be an object');
  }

  const pvqData = data as PVQData;
  const errors: string[] = [];

  if (!pvqData.userId) errors.push('userId is required');
  if (!pvqData.questionId) errors.push('questionId is required');
  if (!pvqData.answer) errors.push('answer is required');

  if (errors.length > 0) {
    throw new ValidationError('Invalid PVQ data: ' + errors.join('; '), errors);
  }
};

export const validatePaymentData = (userId: unknown, courseId: unknown, amount: unknown, paymentType: unknown): void => {
  const errors: string[] = [];
  const validPaymentTypes = Object.values(PAYMENT_TYPES);

  if (!userId || typeof userId !== 'string') errors.push('userId is required');
  if (!courseId || typeof courseId !== 'string') errors.push('courseId is required');
  if (typeof amount !== 'number' || amount <= 0) errors.push('amount must be a positive number');
  if (!paymentType || typeof paymentType !== 'string') errors.push('paymentType is required');
  if (paymentType && typeof paymentType === 'string' && !validPaymentTypes.includes(paymentType)) {
    errors.push(`paymentType must be one of: ${validPaymentTypes.join(', ')}`);
  }

  if (errors.length > 0) {
    throw new ValidationError('Invalid payment data: ' + errors.join('; '), errors);
  }
};

export const validateProgressData = (userId: unknown, courseId: unknown, totalLessons: unknown): void => {
  const errors: string[] = [];

  if (!userId || typeof userId !== 'string') errors.push('userId is required');
  if (!courseId || typeof courseId !== 'string') errors.push('courseId is required');
  if (typeof totalLessons !== 'number' || totalLessons < 1) {
    errors.push('totalLessons must be a positive number');
  }

  if (errors.length > 0) {
    throw new ValidationError('Invalid progress data: ' + errors.join('; '), errors);
  }
};

interface LessonData {
  courseId?: string;
  moduleId?: string;
  title?: string;
  [key: string]: unknown;
}

export const validateLessonData = (lessonData: unknown): void => {
  if (!lessonData || typeof lessonData !== 'object' || Array.isArray(lessonData)) {
    throw new ValidationError('Lesson data must be an object');
  }

  const data = lessonData as LessonData;
  const errors: string[] = [];

  if (!data.courseId) errors.push('courseId is required');
  if (!data.moduleId) errors.push('moduleId is required');
  if (!data.title) errors.push('title is required');

  if (errors.length > 0) {
    throw new ValidationError('Invalid lesson data: ' + errors.join('; '), errors);
  }
};

interface ModuleData {
  courseId?: string;
  title?: string;
  [key: string]: unknown;
}

export const validateModuleData = (moduleData: unknown): void => {
  if (!moduleData || typeof moduleData !== 'object' || Array.isArray(moduleData)) {
    throw new ValidationError('Module data must be an object');
  }

  const data = moduleData as ModuleData;
  const errors: string[] = [];

  if (!data.courseId) errors.push('courseId is required');
  if (!data.title) errors.push('title is required');

  if (errors.length > 0) {
    throw new ValidationError('Invalid module data: ' + errors.join('; '), errors);
  }
};

interface CourseData {
  title?: string;
  description?: string;
  [key: string]: unknown;
}

export const validateCourseData = (courseData: unknown): void => {
  if (!courseData || typeof courseData !== 'object' || Array.isArray(courseData)) {
    throw new ValidationError('Course data must be an object');
  }

  const data = courseData as CourseData;
  const errors: string[] = [];

  if (!data.title) errors.push('title is required');
  if (!data.description) errors.push('description is required');

  if (errors.length > 0) {
    throw new ValidationError('Invalid course data: ' + errors.join('; '), errors);
  }
};

interface TimeSlotData {
  instructor?: string;
  startTime?: string | number;
  endTime?: string | number;
  [key: string]: unknown;
}

export const validateTimeSlotData = (slotData: unknown): void => {
  if (!slotData || typeof slotData !== 'object' || Array.isArray(slotData)) {
    throw new ValidationError('Time slot data must be an object');
  }

  const data = slotData as TimeSlotData;
  const errors: string[] = [];

  if (!data.instructor) errors.push('instructor is required');
  if (!data.startTime) errors.push('startTime is required');
  if (!data.endTime) errors.push('endTime is required');

  if (errors.length > 0) {
    throw new ValidationError('Invalid time slot data: ' + errors.join('; '), errors);
  }
};

/**
 * Validates US phone numbers in various common formats
 * @param {string} phoneNumber - The phone number to validate (required, non-empty)
 * @param {boolean} [allowExtensions=false] - Whether to allow extensions (x123, ext 123, extension 123)
 * @throws {ValidationError} If phone number format is invalid
 * @example
 * validatePhoneNumber('(212) 456-7890'); // Valid
 * validatePhoneNumber('+1 (617) 555-7890'); // Valid
 */
export const validatePhoneNumber = (phoneNumber: unknown, allowExtensions: boolean = false): void => {
  if (!phoneNumber || typeof phoneNumber !== 'string' || phoneNumber.trim().length === 0) {
    throw new ValidationError('Invalid phone number format');
  }

  let cleanedNumber = phoneNumber.trim();

  // Handle extensions if allowed
  if (allowExtensions) {
    // Remove extension patterns: x123, ext 123, extension 123
    cleanedNumber = cleanedNumber.replace(/\s*(x|ext|extension)\s*\d+\s*$/i, '');
  }

  // Check for extensions when not allowed (before removing non-digits)
  if (!allowExtensions) {
    const hasExtension = /\s*(x|ext|extension)\s*\d+/i.test(phoneNumber);
    if (hasExtension) {
      throw new ValidationError('Invalid phone number format');
    }
  }

  // Remove all non-digit characters except + at the start
  const hasPlus = cleanedNumber.startsWith('+');
  const digitsOnly = cleanedNumber.replace(/\D/g, '');

  // Validate digit count
  // Should be exactly 10 digits, or 11 if it starts with 1 (for +1 country code)
  if (!(digitsOnly.length === 10 || (digitsOnly.length === 11 && digitsOnly.startsWith('1')))) {
    throw new ValidationError('Invalid phone number format');
  }

  // Extract area code (first 3 digits of the 10-digit number)
  const areaCode = digitsOnly.length === 11 ? digitsOnly.substring(1, 4) : digitsOnly.substring(0, 3);
  const firstDigit = parseInt(areaCode.charAt(0), 10);

  // Validate area code (cannot start with 0 or 1)
  if (firstDigit === 0 || firstDigit === 1) {
    throw new ValidationError('Invalid phone number format');
  }

  // Validate country code if present
  if (hasPlus) {
    const countryCode = digitsOnly.substring(0, 1);
    if (countryCode !== '1') {
      throw new ValidationError('Invalid phone number format');
    }
  }
};
